import * as THREE from "https://cdn.jsdelivr.net/npm/three@0.155.0/build/three.module.js";
import { GLTFLoader } from "https://cdn.jsdelivr.net/npm/three@0.155.0/examples/jsm/loaders/GLTFLoader.js";
import { RGBELoader } from "https://cdn.jsdelivr.net/npm/three@0.155.0/examples/jsm/loaders/RGBELoader.js";

const clock = new THREE.Clock();
let bounceTime = 0;
const bounceSpeed = 2.5;     // animation speed
const bounceHeight = 0.03;   // how high it moves (very subtle)
let baseY = 0;

// const isMobile = window.innerWidth <= 640;
let dragMode = null; // "rotate" or "scroll"
let watchPivot;   
let introActive = false;
let introStartTime = 0;
const introDuration = 1.6;
let lastIntroTime = 0;              // tracks last time intro played
const INTRO_COOLDOWN = 20000; 

function isMobileView() {
  return window.innerWidth <= 640;
}

// TIMEZONE HELPER 
function getHMSInZone(timeZone) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
    timeZone
  });

  const parts = fmt.formatToParts(new Date());
  const pick = (t) => Number(parts.find((p) => p.type === t).value);

  return {
    h: pick("hour"),
    m: pick("minute"),
    s: pick("second")
  };
}

// Get HTML elements
const container = document.getElementById("watch-canvas");
const loaderEl = document.getElementById("watch-loader");


const observer = new IntersectionObserver(([entry]) => {

  if (!watch || !watchPivot) return;

  if (entry.isIntersecting) {

    const now = Date.now();

    if (now - lastIntroTime < INTRO_COOLDOWN) return;

    lastIntroTime = now;

    isDragging = false;

    gsap.to(watch.rotation, {
      x: 0,
      y: 0,
      duration: 0.6,
      ease: "power3.out",
      onComplete: () => {
        introActive = true;
        introStartTime = clock.getElapsedTime();
      }
    });

  } else {

    // 👇 RESET WHEN LEAVING SECTION

    introActive = false;

    gsap.to(watch.rotation, {
      x: 0,
      y: 0,
      duration: 0.5,
      ease: "power2.out"
    });

    gsap.to(watchPivot.rotation, {
      y: 0,
      duration: 0.5,
      ease: "power2.out"
    });

    gsap.to(watchPivot.scale, {
      x: 1,
      y: 1,
      z: 1,
      duration: 0.4,
      ease: "power2.out"
    });

  }

}, { threshold: 0.6 });

observer.observe(container);



// Safety check
if (!container) {
  console.error("watch-canvas not found");
  throw new Error("Missing #watch-canvas");
}

// Scene
const scene = new THREE.Scene();

// Camera
const camera = new THREE.PerspectiveCamera(
  45,
  window.innerWidth / window.innerHeight,
  0.1,
  1000
);
camera.position.set(0, 0, 5);
camera.lookAt(0, 0, 0);

// Renderer
const renderer = new THREE.WebGLRenderer({
  antialias: true,
  alpha: false
});

container.appendChild(renderer.domElement);

renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2;
renderer.physicallyCorrectLights = true;

// 🌅 HDR Environment (MOVE IT HERE)
const pmremGenerator = new THREE.PMREMGenerator(renderer);
pmremGenerator.compileEquirectangularShader();

new RGBELoader()
  .setDataType(THREE.FloatType)
  .load("https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/studio_small_09_1k.hdr", function (texture) {
    const envMap = pmremGenerator.fromEquirectangular(texture).texture;
    scene.environment = envMap;
    scene.environmentIntensity = 1.5;  // was 0.8

    texture.dispose();
    pmremGenerator.dispose();
  });


renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
renderer.setClearColor("#000000", 1);

// Lights
// Soft fill
const ambient = new THREE.AmbientLight(0xffffff, 0.7);
scene.add(ambient);

// Key light (front-right)
const keyLight = new THREE.DirectionalLight(0xffffff, 2);
keyLight.position.set(10, 4, 6);
scene.add(keyLight);

// Rim light (back-left)
const rimLight = new THREE.DirectionalLight(0xffffff, 1.2);
rimLight.position.set(-6, 4, -8);
scene.add(rimLight);

//MATERIAL PRESETS

const polishedSteel = {
  color: "#c9ced8",
  metalness: 1.4,
  roughness: 0.02,
  clearcoat: 0.5,
  clearcoatRoughness: 0.26,
  envMapIntensity: 2.2
};

const brushedSteel = {
  color: "#c9ced8",
  metalness: 1,
  roughness: 0.09,
  clearcoat: 0.9,
  clearcoatRoughness: 0.06,
  envMapIntensity: 1.4
};








// 🎨 MATERIAL CONTROL FUNCTIONS
function setDialColor(color) {
  if (!window._watchMaterials) return;

  const { dialMesh, dialPrint } = window._watchMaterials;

  const normalized = color.toLowerCase();

  const isWhite = normalized === "#ffffff";
  const isBlack = normalized === "#000000";

  // 👇 Replace pure black with dark brown
  const baseColor = isBlack ? "#1b120c" : color;

  const contrastColor = isWhite ? "#111111" : "#ffffff";

  // --- Base Dial ---
  dialMesh.material = new THREE.MeshPhysicalMaterial({
    color: baseColor,

    metalness: isWhite ? 0.3 : 0.4,   // reduce metalness for dark dial
    roughness: isBlack ? 0.45 : 0.35, // slightly softer for brown dial

    clearcoat: 0.15,
    clearcoatRoughness: 0.25,

    envMapIntensity: 1.0
  });

  dialMesh.material.needsUpdate = true;

  // --- Print Layer (logo + numbers) ---
  dialPrint.material = new THREE.MeshPhysicalMaterial({
    color: contrastColor,
    metalness: 0,
    roughness: 0.4,
    clearcoat: 0.1,
    clearcoatRoughness: 0.4,
    envMapIntensity: 0.8,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1
  });

  dialPrint.material.needsUpdate = true;
}



function setStrapColor(color) {
  const { strapMesh1, strapMesh2 } = window._watchMaterials || {};
  if (!strapMesh1 || !strapMesh2) return;

  strapMesh1.material.color.set(color);
  strapMesh2.material.color.set(color);

  strapMesh1.material.needsUpdate = true;
  strapMesh2.material.needsUpdate = true;
}



//3D model is here


const loader = new GLTFLoader();


let watch;
let hourHand;
let minuteHand;
let secondHand;
let hourBaseRotation = 0;
let minuteBaseRotation = 0;
let secondBaseRotation = 0;

// let watchPivot;
let currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;



loader.load(
  "https://cdn.shopify.com/3d/models/7c4f6f2e60fa2b42/watchsilicone.glb",

  // ✅ SUCCESS
(gltf) => {

  watch = gltf.scene;
  // 🎯 Target hand GROUPS (not meshes)
hourHand = watch.getObjectByName("hour_hand002");
minuteHand = watch.getObjectByName("minute_hand002");
secondHand = watch.getObjectByName("seconds002");

let currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;








  const isMobile = window.innerWidth <= 640;

const scale = isMobile ? 0.035 : 0.05;

watch.scale.set(scale, scale, scale);

  // 2️⃣ Compute bounding box AFTER scaling
  const box = new THREE.Box3().setFromObject(watch);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());

  // 3️⃣ Center model
  watch.position.sub(center);

  
 // 4️⃣ Add to scene
 watchPivot = new THREE.Group();
watchPivot.add(watch);
scene.add(watchPivot);

baseY = watch.position.y;
const basePosition = watch.position.clone();
window._basePosition = basePosition;


// 🔍 DEBUG: Find all glass meshes
watch.traverse((child) => {
  if (child.name.toLowerCase().includes("glass")) {
    console.log("GLASS FOUND:", child.name, child);
  }
});



// 🧊 REMOVE HDR REFLECTION FROM ALL GLASS MESHES
// const glass1 = watch.getObjectByName("glass002_1");
// const glass2 = watch.getObjectByName("glass002_2");

// [glass1, glass2].forEach((glass) => {
//   if (!glass) return;

//   glass.material = new THREE.MeshPhysicalMaterial({
//     color: 0xffffff,
//     transmission: 1,
//     thickness: 1.2,
//     roughness: 0.04,
//     metalness: 0,
//     clearcoat: 0,
//     envMapIntensity: 0   // 🔥 disables HDR reflection
//   });

//   glass.material.needsUpdate = true;
// });

// const glass1 = watch.getObjectByName("glass002_1");
// const glass2 = watch.getObjectByName("glass002_2");

// [glass1, glass2].forEach((glass) => {
//   if (!glass) return;

//   glass.material = new THREE.MeshPhysicalMaterial({
//     color: 0xffffff,
//     transmission: 1,           // full transparency
//     thickness: 1.6,            // same as React
//     ior: 1.76,                 // refractive index
//     roughness: 0.03,
//     metalness: 0,
//     specularIntensity: 0.85,
//     attenuationColor: new THREE.Color("#9fb8ff"),
//     attenuationDistance: 10,
//     envMapIntensity: 0.2,
//     clearcoat: 0,
//     depthWrite: false
//   });

//   glass.material.needsUpdate = true;
// });

// ===== REACT-STYLE GLASS (NO HDR REFLECTION) =====

const glass = watch.getObjectByName("glass002_2");

if (glass) {
  glass.material = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,

    transmission: 1,        // full transparency
    thickness: 1.6,         // same as React
    ior: 1.76,              // refractive index
    roughness: 0.03,
    metalness: 0,

    specularIntensity: 0.85,

    attenuationColor: new THREE.Color("#9fb8ff"),
    attenuationDistance: 10,

    envMapIntensity: 0,     // 🔥 removes HDR reflection

    clearcoat: 0,
    
    depthWrite: false
  });

  glass.material.needsUpdate = true;
  glass.material.envMap = null;
glass.material.envMapIntensity = 0;
glass.material.reflectivity = 0;
glass.material.metalness = 0;

}

const unwantedGlass = watch.getObjectByName("glass002_1");

if (unwantedGlass) {
  unwantedGlass.visible = false;   // 👈 completely hides it
}




  // 🎨 TARGET MATERIAL MESHES
const dialMesh = watch.getObjectByName("dial002_1");
const dialPrint = watch.getObjectByName("dial002_2");

console.log("Dial mesh:", dialMesh);

const strapMesh1 = watch.getObjectByName("BUCKLE_strap003");
const strapMesh2 = watch.getObjectByName("BUKCLE_strap004");



const dialTop = watch.getObjectByName("dial002_2");
// const minuteRing = watch.getObjectByName("dial002_3");
const lumeRing = watch.getObjectByName("dial002_4");

if (lumeRing) {
  lumeRing.material = new THREE.MeshPhysicalMaterial({
    // color: "#4DFF5B",
    color: "#00910B",
    metalness: 0,
    roughness: 0.4,
    emissive: "#9fffa7",
    emissiveIntensity: 0.1,
    clearcoat: 0.1,
    envMapIntensity: 0.8,

    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2
  });

  lumeRing.material.needsUpdate = true;
}


const hourLume = watch.getObjectByName("hour_hand002_2");
const minuteLume = watch.getObjectByName("minute_hand002_2");

[hourLume, minuteLume].forEach((lume) => {
  if (!lume) return;

  lume.material = new THREE.MeshPhysicalMaterial({
    color: "#9CFFA4",
    metalness: 0,
    roughness: 0.4,
    emissive: "#9fffa7",
    emissiveIntensity: 0.05,
    clearcoat: 0.1,
    envMapIntensity: 0.8,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2
  });

  lume.material.needsUpdate = true;
});

// const hourHandSteel = watch.getObjectByName("hour_hand002_1");
const minuteHandLume = watch.getObjectByName("minute_hand002_2");


// if (hourHandSteel) {
//   hourHandSteel.material = new THREE.MeshPhysicalMaterial({
//     color: "#c9ced8",
//     metalness: 1,
//     roughness: 0.18,
//     clearcoat: 0.3,
//     clearcoatRoughness: 0.25,
//     envMapIntensity: 1.2
//   });

//   hourHandSteel.material.needsUpdate = true;
// }


if (minuteHandLume) {
  minuteHandLume.material = new THREE.MeshPhysicalMaterial({
    color: "#4DFF5B",
    metalness: 0,
    roughness: 0.4,
    emissive: "#9fffa7",
    emissiveIntensity: 0.5,
    clearcoat: 0.1,
    envMapIntensity: 0.8,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2
  });

  minuteHandLume.material.needsUpdate = true;


}



const minuteRing = watch.getObjectByName("dial002_3");

if (minuteRing) {
  minuteRing.material = new THREE.MeshPhysicalMaterial({
    color: "#ECEEF1",
    metalness: 1,
    roughness: 0.35,
    clearcoat: 0.5,
    clearcoatRoughness: 0.26,
    envMapIntensity: 1,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
    depthTest: true
  });

  minuteRing.material.needsUpdate = true;
}

const mainCase = watch.getObjectByName("main_case002");
const innerBezel = watch.getObjectByName("inner_bezel002");

if (mainCase) {
  mainCase.material = new THREE.MeshPhysicalMaterial({
    color: "#ffffff",
    metalness: 1,
    roughness: 0.18,
    clearcoat: 0.4,
    clearcoatRoughness: 0.2,
    envMapIntensity: 1.4
  });
}

if (innerBezel) {
  innerBezel.material = new THREE.MeshPhysicalMaterial({
    color: "#000000",
    metalness: 1,
    roughness: 0.08,
    clearcoat: 0.5,
    clearcoatRoughness: 0.15,
    envMapIntensity: 1.6
  });
}

const bezel = watch.getObjectByName("bezel002");

if (bezel) {
  bezel.material = new THREE.MeshPhysicalMaterial({
    color: "#120B04",   // deep black
    metalness: 0.9,
    roughness: 0.35,
    clearcoat: 0.4,
    clearcoatRoughness: 0.2,
    envMapIntensity: 1.0
  });

  bezel.material.needsUpdate = true;
}

const outerCase = watch.getObjectByName("main_case002");

if (outerCase) {
  outerCase.material = new THREE.MeshPhysicalMaterial({
    color: "#BABDC3",   // steel tone
    metalness: 1,
    roughness: 0.28,    // brushed feel
    clearcoat: 0.3,
    clearcoatRoughness: 0.25,
    envMapIntensity: 1.2
  });

  outerCase.material.needsUpdate = true;
}

const secondHandMesh = watch.getObjectByName("seconds002");
const hourHandSteel = watch.getObjectByName("hour_hand002_1");
const minuteHandSteel = watch.getObjectByName("minute_hand002_1");

[hourHandSteel, minuteHandSteel].forEach(hand => {
  if (!hand) return;

  hand.material = new THREE.MeshPhysicalMaterial({
    color: "#d6dbe3",
    metalness: 1,
    roughness: 0.38,
    clearcoat: 0.25,
    clearcoatRoughness: 0.3,
    envMapIntensity: 1.2
  });

  hand.material.needsUpdate = true;
});


if (secondHandMesh) {
  secondHandMesh.material = new THREE.MeshPhysicalMaterial({
    color: "#d6dbe3",
    metalness: 1,
    roughness: 0.38,
    clearcoat: 0.25,
    clearcoatRoughness: 0.3,
    envMapIntensity: 1.2
  });

  secondHandMesh.material.needsUpdate = true;
}

const backText = watch.getObjectByName("UNDER_COVER003_1");

if (backText) {
  backText.material = new THREE.MeshPhysicalMaterial({
    color: "#d6dbe3",
    metalness: 1,
    roughness: 0.4,
    clearcoat: 0.2,

    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2
  });

  backText.material.needsUpdate = true;
}






// Save globally so buttons can access
window._watchMaterials = {
  dialMesh,
  dialPrint,
  strapMesh1,
  minuteRing,
  strapMesh2,
  lumeRing,
  hourLume,
  minuteLume,
  backText,
   
  minuteHandLume,
  mainCase,
  innerBezel,
  bezel,
  outerCase,
  secondHandMesh,
  hourHandSteel,
  minuteHandSteel

};
// 🎯 DEFAULT STRAP COLOR ON LOAD
const defaultStrapColor = "#23291D"; // your green
setStrapColor(defaultStrapColor);

// Set default active UI button
const defaultStrapButton = document.querySelector('.strap-option[data-color="#23291D"]');
if (defaultStrapButton) {
  defaultStrapButton.classList.add("active");
}



// 🎯 DEFAULT DIAL COLOR ON LOAD
const defaultDialColor = "#ffffff";

setDialColor(defaultDialColor);

// Set default active UI button
const defaultDialButton = document.querySelector('.dial-option[data-color="#ffffff"]');

if (defaultDialButton) {
  defaultDialButton.classList.add("active");
}

// Move slider to white position
const thumb = document.getElementById("dial-thumb");
if (thumb) {
  thumb.style.transform = "translateX(0%)";
  thumb.style.background = "white";
}


console.log("Materials stored:", window._watchMaterials);




  // 5️⃣ Camera framing
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
  cameraZ *= 1.5;

  camera.position.set(0, 0, cameraZ * 1.3);
  camera.lookAt(0, 0, 0);

  // 6️⃣ Material safety
  watch.traverse((child) => {
    if (child.isMesh) {
      child.material.side = THREE.DoubleSide;
      child.material.needsUpdate = true;
    }
  });

  watch.traverse((child) => {
  if (child.isMesh) {
    console.log("MESH:", child.name);
  }
});




  if (loaderEl) loaderEl.style.display = "none";



console.log("MODEL CENTERED");

  },

  // ✅ PROGRESS
  (xhr) => {
    if (!xhr.total) return;
    const percent = Math.round((xhr.loaded / xhr.total) * 100);
    console.log("LOADING:", percent);
  },

  // ❌ ERROR
  (error) => {
    console.error("GLTF LOAD ERROR:", error);
  }
);






let isDragging = false;
let previousX = 0;
let previousY = 0;

container.addEventListener("pointerdown", (e) => {
  isDragging = true;
  dragMode = null; // 🔥 reset mode

  container.classList.add("dragging");

  previousX = e.clientX;
  previousY = e.clientY;
});

window.addEventListener("pointerup", () => {
  isDragging = false;
  container.classList.remove("dragging");
});
const ROTATION_SPEED = 0.005;
const MAX_TILT = Math.PI / 4;

window.addEventListener("pointermove", (e) => {
  if (!isDragging || !watch) return;

  const deltaX = e.clientX - previousX;
  const deltaY = e.clientY - previousY;

  const absX = Math.abs(deltaX);
  const absY = Math.abs(deltaY);

  // 🔥 Decide gesture ONCE
  if (!dragMode) {
    if (absX > absY) {
      dragMode = "rotate";
    } else {
      dragMode = "scroll";
    }
  }

  // 🎯 ROTATE ONLY IF LOCKED
  if (dragMode === "rotate") {
    e.preventDefault();

    watch.rotation.y += deltaX * ROTATION_SPEED;

    watch.rotation.x += deltaY * ROTATION_SPEED * 0.3;

    watch.rotation.x = THREE.MathUtils.clamp(
      watch.rotation.x,
      -MAX_TILT,
      MAX_TILT
    );
  }

  // 👉 if scroll mode → do NOTHING (browser scrolls naturally)

  previousX = e.clientX;
  previousY = e.clientY;

}, { passive: false });




  // ===== STRAP LOGIC =====
  const titleEl = document.getElementById("watch-title");
  const strapButtons = document.querySelectorAll(".strap-option");

  strapButtons.forEach(btn => {
    btn.addEventListener("click", () => {

      strapButtons.forEach(b => b.classList.remove("active"));
      btn.classList.add("active");

      const color = btn.dataset.color;
      const name = btn.dataset.name;

      setStrapColor(color);

      if (titleEl) {
        titleEl.textContent =
          `The Big Brags Silicone watch in ${name} Strap`;
      }

    });
  });


  // ===== DIAL LOGIC =====
  document.addEventListener("DOMContentLoaded", function() {
const buildBtn = document.getElementById("build-watch-btn");

if (buildBtn) {
  buildBtn.addEventListener("click", () => {

    console.log("Selected Strap:", selectedStrap);
    console.log("Selected Dial:", selectedDial);

    const key = `${selectedStrap}-${selectedDial}`;
    console.log("Variant key:", key);

    const variantId = variantMap[key];
    console.log("Variant ID:", variantId);

    if (!variantId) {
      alert("Variant not found");
      return;
    }

    window.location.href =
      `/products/brags-silicone-morph-watch-1?variant=${variantId}`;

  });
}
    // ===== GET PRODUCT DATA FROM SHOPIFY =====
const productDataEl = document.getElementById("product-data");

const variantMap = {

  "Black-Black": 44498484068386,
  "Blue-Black": 44498484101154,
  "Green-Black": 44498484133922,

  "Black-White": 44498488655906,
  "Blue-White": 44498488688674,
  "Green-White": 44498488721442

};
let selectedStrap = "Green";  // default
let selectedDial = "White";   // default

if (productDataEl) {
  const productData = JSON.parse(productDataEl.textContent);

  productData.variants.forEach(variant => {
    const key = `${variant.option1}-${variant.option2}`;
    variantMap[key] = variant.id;
  });

  console.log("Variant Map:", variantMap);
}

    // ===== GET PRODUCT DATA FROM SHOPIFY =====
// const productDataEl = document.getElementById("product-data");

// let variantMap = {};

if (productDataEl) {
  const productData = JSON.parse(productDataEl.textContent);

  productData.variants.forEach(variant => {

  const strap = variant.option1.trim();
  const dial = variant.option2.trim();

  const key = `${strap}-${dial}`;

  variantMap[key] = variant.id;

});

  console.log("Variant Map:", variantMap);
}

  const thumb = document.getElementById("dial-thumb");
  const dialButtons = document.querySelectorAll(".dial-option");

 

//   dialButtons.forEach(btn => {
//     btn.addEventListener("click", () => {

//       // Remove active from all
//       dialButtons.forEach(b => b.classList.remove("active",));
//       btn.classList.add("active");

//       const value = btn.dataset.value;
//       const color = btn.dataset.color;

//       // Move slider
//       if (value === "black") {
//         thumb.style.transform = "translateX(100%)";
//         thumb.style.background = "#111";
//         btn.style.color = "white";
//       } else {
//         thumb.style.transform = "translateX(0%)";
//         thumb.style.background = "white";
//         btn.style.color = "black";
        

//       }

//       setDialColor(color);
//     });
//   });

// });


dialButtons.forEach(btn => {
  btn.addEventListener("click", () => {

    dialButtons.forEach(b => {
      b.classList.remove("active", "white-active", "black-active");
    });

    btn.classList.add("active");

    const value = btn.dataset.value;
    const color = btn.dataset.color;

    if (value === "black") {
      thumb.style.transform = "translateX(100%)";
      thumb.style.background = "#111";

      btn.classList.add("black-active");

    } else {
      thumb.style.transform = "translateX(0%)";
      thumb.style.background = "white";

      btn.classList.add("white-active");
    }

    setDialColor(color);
  });
});
})

function animate() {
  requestAnimationFrame(animate);

  if (hourHand && minuteHand && secondHand) {

   const { h, m, s } = getHMSInZone(currentTimeZone);

const seconds = s;
const minutes = m + seconds / 60;
const hours = (h % 12) + minutes / 60;


    const TAU = Math.PI * 2;

    const secondAngle = -(seconds / 60) * TAU;
    const minuteAngle = -(minutes / 60) * TAU;
    const hourAngle = -(hours / 12) * TAU;

    hourHand.rotation.z = hourAngle;
minuteHand.rotation.z = minuteAngle;
secondHand.rotation.z = secondAngle;

// 🎬 Intro Spin Animation
if (watchPivot && introActive) {

  const elapsed = clock.getElapsedTime() - introStartTime;
  const t = Math.min(elapsed / introDuration, 1);

  const eased = t < 0.5
    ? 2 * t * t
    : 1 - Math.pow(-2 * t + 2, 2) / 2;

  const angle = Math.sin(eased * Math.PI) * (Math.PI / 2);

  watchPivot.rotation.y = angle;

  if (t >= 1) {
    introActive = false;
    watchPivot.rotation.y = 0;
  }
}


// ✨ Subtle Bounce Effect
// ✨ Circular Float Animation
if (watch && !isDragging) {
  const elapsed = clock.getElapsedTime();

  const radius = 0.05;     // how big the circle is
  const speed = 0.8;       // speed of movement

  watch.position.x = window._basePosition.x + Math.cos(elapsed * speed) * radius;
  watch.position.y = window._basePosition.y + Math.sin(elapsed * speed) * radius;
}

  }

  renderer.render(scene, camera);
}



animate();



// 🔁 HANDLE RESIZE (VERY IMPORTANT FOR SHOPIFY)
window.addEventListener("resize", () => {
  const rect = container.getBoundingClientRect();

  camera.aspect = rect.width / rect.height;
  camera.updateProjectionMatrix();
  renderer.setSize(rect.width, rect.height);

  // 🔥 ADD THIS
  if (watch) {
    const isMobile = window.innerWidth <= 640;
    const scale = isMobile ? 0.035 : 0.05;

    watch.scale.set(scale, scale, scale);
  }
});


document.addEventListener("click", (e) => {
  if (e.target.classList.contains("dial-color")) {
    const color = e.target.dataset.color;
    setDialColor(color);
  }

  if (e.target.classList.contains("strap-color")) {
    const color = e.target.dataset.color;
    setStrapColor(color);
  }
});

// Dial UI Logic




